import React, { useState } from "react";
import Heading from "./partials/Heading";
import "./Projects.scss";
import cn from "classnames";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slick from "react-slick";
import {
  projectsSelectors,
  ProjectTag,
  selectImagesByProjectID,
  selectTagsByProjectID,
  useAppSelector,
} from "../store";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { pageVariants } from "../App";
import { EntityId } from "@reduxjs/toolkit";

interface SearchBarProps {
  value: string;
  onChange?: (searchStr: string) => void;
}
function SearchBar({ onChange, value }: SearchBarProps) {
  return (
    <div className="search-bar">
      <img src="/assets/images/magnifying-glass.svg" alt="search icon" />
      <input
        placeholder="Search"
        onChange={(ev) => onChange?.(ev.target.value)}
        value={value}
      />
    </div>
  );
}

const projectCardVariants: Variants = {
  initial: { scale: 0.25, opacity: 0 },
  in: { scale: 1, opacity: 1 },
  out: { scale: 0, opacity: 0 },
};
interface ProjectCardProps {
  searchStr?: string;
  selectedTags?: ProjectTag[];
  id: EntityId;
  onExclude?: (id: EntityId, exclude: boolean) => void;
}

function ProjectCard({ id, searchStr, onExclude }: ProjectCardProps) {
  const imagesSrc = useAppSelector((state) =>
    selectImagesByProjectID(state, id)
  );
  const project = useAppSelector((state) =>
    projectsSelectors.selectById(state, id)
  );
  const tags = useAppSelector((state) => selectTagsByProjectID(state, id));

  const nameFixed = project?.name.toLowerCase() ?? "";
  const searchStrFixed = searchStr?.trim().toLowerCase();

  let show: true | null = null;
  if (
    searchStrFixed &&
    nameFixed.indexOf(searchStrFixed) < 0 &&
    tags.findIndex(
      (item) => item.name.toLowerCase().indexOf(searchStrFixed) > -1
    ) < 0
  ) {
    show = null;
  } else {
    show = true;
  }
  // eslint-disable-next-line

  return show && project && project.show ? (
    <motion.div
      className="project-card"
      initial="initial"
      animate="in"
      exit="out"
      layout
      variants={projectCardVariants}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      {imagesSrc && imagesSrc.length > 0 && (
        <div className="preview">
          <Slick arrows={false} autoplay autoplaySpeed={5000}>
            {imagesSrc &&
              imagesSrc.map((item, index) => (
                <img src={item.src} key={index} alt={item.name} />
              ))}
          </Slick>
        </div>
      )}

      <div className="title">{project.name}</div>
      <div className="summary">
        {project.briefDescription ?? project.fullDescription}
      </div>
      <div className="icons-container">
        <div
          className={cn("icon", { "icon-disabled": !project.githubRepo })}
          title={
            project.githubRepo
              ? "Open this project's Github Repository."
              : "This site doesn't have a Github Repository or is closed-sourced."
          }
        >
          <a
            href={project.githubRepo}
            target="_blank"
            rel="noreferrer npopoener"
          >
            <img
              className="icon-img"
              src="/assets/images/github.svg"
              alt="github icon"
            />
          </a>
        </div>

        <div
          className={cn("icon", { "icon-disabled": !project.liveSite })}
          title={
            project.liveSite
              ? "See this project's live website."
              : "This site doesn't have a live website."
          }
        >
          <a href={project.liveSite} target="_blank" rel="noreferrer npopoener">
            <img
              className="icon-img"
              src="/assets/images/eye.svg"
              alt="eye icon"
            />
          </a>
        </div>

        <div className={cn("icon", { "icon-disabled": false })}>
          <img
            className="icon-img"
            src="/assets/images/external-link.svg"
            alt="external icon"
            title="View project in new tab."
          />
        </div>
      </div>
      {tags && tags.length > 0 && (
        <div className="tags-container">
          {tags.map((item, index) => {
            return (
              <div className="tag" key={index}>
                <div
                  style={{ backgroundColor: item.color }}
                  className="tag-body"
                >
                  {item.name}
                </div>
                <div
                  className="tag-triangle"
                  style={{ borderLeft: `10px solid ${item.color}` }}
                ></div>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  ) : null;
}

interface ProjectsProps {}
function Projects(props: ProjectsProps) {
  const [searchStr, setSearchStr] = useState("");
  const projectsIDs = useAppSelector(projectsSelectors.selectIds);

  return (
    <motion.div
      className="projects-page"
      variants={pageVariants}
      initial="initial"
      animate="in"
      exit="out"
    >
      <Heading>projects</Heading>
      {false && <SearchBar value={searchStr} onChange={setSearchStr} />}
      <div className="projects-container">
        <AnimatePresence>
          {projectsIDs &&
            projectsIDs.map((item, index) => {
              return (
                <ProjectCard
                  key={index}
                  id={item}
                  searchStr={searchStr}
                  onExclude={(id, exclude) => {}}
                />
              );
            })}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
export { Projects as default };
